import React, { FC, useContext } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ProfessionalInfoSection } from './components/ProfessionalInfoSection';
import { IntroductionVideoSection } from './components/IntroductionVideoSection';
import { CareerHighlightsSection } from './components/CareerHighlightsSection';
import { ExpertiseSection } from './components/ExpertiseSection';
import { TrainingStyleSection } from './components/TrainingStyleSection';
import { SessionPricingSection } from './components/SessionPricingSection';
import { LocationFacilitySection } from './components/LocationFacilitySection';
import { ScheduleAvailabilitySection } from './components/ScheduleAvailabilitySection';
import { SocialLinksSection } from './components/SocialLinksSection';
import { ProfileComingSoon } from './components/ProfileComingSoon';
import { ProfileEditProvider, ProfileEditContext } from './ProfileEditContext';
import './Profile.scss';
import { ReactComponent as Pencil2Icon } from '../../../../../../assets/images/onboarding-trainer/dashboard/profile/pencil2.svg';

type ProfileTab = 'profile' | 'packages' | 'settings';

const ProfileContent: FC = () => {
  const { isBulkEditing, toggleAllEdits } = useContext(ProfileEditContext);
  const [searchParams, setSearchParams] = useSearchParams();

  const currentTabParam = searchParams.get('tab');
  const activeTab: ProfileTab =
    currentTabParam === 'packages'
      ? 'packages'
      : currentTabParam === 'settings'
        ? 'settings'
        : 'profile';

  const handleTabChange = (tab: ProfileTab) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (tab === 'profile') {
        next.delete('tab');
      } else {
        next.set('tab', tab);
      }
      return next;
    });
  };

  return (
    <div className="profile-container">
      {/* Top Tab Bar for Mobile & Desktop */}
      <div className="profile-top-tabs-bar">
        <div className="profile-tabs-nav">
          <button
            type="button"
            className={`profile-tab-btn ${activeTab === 'profile' ? 'active' : ''}`}
            onClick={() => handleTabChange('profile')}
          >
            {'My Profile'}
          </button>
          <div className="tab-separator" />
          <button
            type="button"
            className={`profile-tab-btn ${activeTab === 'packages' ? 'active' : ''}`}
            onClick={() => handleTabChange('packages')}
          >
            {'Packages'}
          </button>
          <div className="tab-separator" />
          <button
            type="button"
            className={`profile-tab-btn ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => handleTabChange('settings')}
          >
            {'Settings'}
          </button>
        </div>
      </div>

      {activeTab === 'profile' && (
        <>
          <div className="profile-header">
            <div className="header-text">
              <h1>{'Profile'}</h1>
              <p>{'Manage your account information'}</p>
            </div>
            <button className="edit-profile-btn" onClick={toggleAllEdits}>
              {<Pencil2Icon />}
              {isBulkEditing ? 'Cancel Editing' : 'Edit Profile'}
            </button>
          </div>

          <div className="profile-content">
            <ProfessionalInfoSection />
            <IntroductionVideoSection />
            <CareerHighlightsSection />
            <ExpertiseSection />
            <TrainingStyleSection />
            <SessionPricingSection />
            <LocationFacilitySection />
            <ScheduleAvailabilitySection />
            <SocialLinksSection />
          </div>
        </>
      )}

      {activeTab === 'packages' && (
        <ProfileComingSoon
          title="Packages"
          subtitle="Create, manage, and offer tailored fitness and coaching packages to your clients directly from your dashboard."
          icon="packages"
        />
      )}

      {activeTab === 'settings' && (
        <ProfileComingSoon
          title="Settings"
          subtitle="Manage your account preferences, notification preferences, and platform configurations in one place."
          icon="settings"
        />
      )}
    </div>
  );
};

export const Profile: FC = () => {
  return (
    <ProfileEditProvider>
      <ProfileContent />
    </ProfileEditProvider>
  );
};
